import React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Avatar,
  Divider,
  Typography,
  IconButton,
  Collapse,
  Box
} from "@mui/material";
import { ExpandLess, ExpandMore, ChevronRight } from "@mui/icons-material";
import { useTheme } from "@mui/system";

const AutobiaList = ({
  items = [],
  dense = false,
  dividers = true,
  disablePadding = false,
  nested = false,
  sx = {},
}) => {
  const theme = useTheme();

  return (
    <List
      dense={dense}
      disablePadding={disablePadding}
      sx={{
        backgroundColor: "white",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.gray.g200}`,
        ...sx,
      }}
    >
      {items.map((item, index) => (
        <AutobiaListItem
          key={item.id || index}
          item={item}
          showDivider={dividers && index < items.length - 1}
          nested={nested}
        />
      ))}
    </List>
  );
};

const AutobiaListItem = ({ item, showDivider, nested }) => {
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);

  const handleClick = () => {
    if (item.children) {
      setOpen(!open);
    }
    if (item.onClick) {
      item.onClick();
    }
  };

  const hasChildren = item.children && item.children.length > 0;

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          ...(nested && { pl: 4 }),
        }}
      >
        <ListItemButton
          onClick={handleClick}
          sx={{
            borderRadius: "4px",
            mx: 0.5,
            "&:hover": {
              backgroundColor: theme.palette.gray.g50,
            },
          }}
        >
          {/* Icon */}
          {item.icon && (
            <ListItemIcon
              sx={{
                color: theme.palette.gray.g600,
                minWidth: 40,
              }}
            >
              {item.icon}
            </ListItemIcon>
          )}

          {/* Avatar */}
          {item.avatar && (
            <ListItemAvatar>
              <Avatar
                src={item.avatar}
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: item.avatarColor || theme.palette.primary.main,
                }}
              >
                {item.avatarText}
              </Avatar>
            </ListItemAvatar>
          )}

          {/* Content */}
          <ListItemText
            primary={
              <Typography
                variant="body1"
                fontWeight={item.bold ? 600 : 400}
                color={item.disabled ? "gray.g400" : "gray.g700"}
              >
                {item.primary}
              </Typography>
            }
            secondary={
              item.secondary && (
                <Typography variant="body2" color="gray.g500">
                  {item.secondary}
                </Typography>
              )
            }
          />

          {/* Trailing Content */}
          {item.trailing && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              {typeof item.trailing === "string" ? (
                <Typography variant="body2" color="gray.g500">
                  {item.trailing}
                </Typography>
              ) : (
                item.trailing
              )}
            </Box>
          )}

          {/* Action */}
          {item.action && (
            <IconButton
              edge="end"
              onClick={(e) => {
                e.stopPropagation();
                if (item.onAction) {
                  item.onAction();
                }
              }}
              sx={{ color: "gray.g400" }}
            >
              {item.action}
            </IconButton>
          )}

          {/* Expand Icon */}
          {hasChildren && (
            <IconButton
              edge="end"
              sx={{ color: "gray.g400" }}
            >
              {open ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}

          {/* Navigate Icon */}
          {item.navigatable && !hasChildren && (
            <ChevronRight sx={{ color: "gray.g400", ml: 1 }} />
          )}
        </ListItemButton>
      </ListItem>

      {/* Nested Items */}
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {item.children.map((child, childIndex) => (
              <AutobiaListItem
                key={child.id || childIndex}
                item={child}
                nested={true}
                showDivider={childIndex < item.children.length - 1}
              />
            ))}
          </List>
        </Collapse>
      )}

      {showDivider && (
        <Divider
          sx={{
            backgroundColor: theme.palette.gray.g100,
            mx: 2,
          }}
        />
      )}
    </>
  );
};

// Specialized List Variants
export const AutobiaMenuList = ({ items, sx = {} }) => {
  return (
    <AutobiaList
      items={items}
      disablePadding={false}
      sx={{
        minWidth: 200,
        boxShadow: 3,
        ...sx,
      }}
    />
  );
};

export const AutobiaContactList = ({ contacts, sx = {} }) => {
  const formattedContacts = contacts.map(contact => ({
    ...contact,
    avatar: contact.avatar,
    primary: contact.name,
    secondary: contact.email || contact.phone,
    trailing: contact.status,
    navigatable: true,
  }));

  return (
    <AutobiaList
      items={formattedContacts}
      sx={sx}
    />
  );
};

export const AutobiaSettingsList = ({ settings, sx = {} }) => {
  const formattedSettings = settings.map(setting => ({
    ...setting,
    icon: setting.icon,
    primary: setting.title,
    secondary: setting.description,
    trailing: setting.value || setting.toggle,
    navigatable: !setting.toggle,
  }));

  return (
    <AutobiaList
      items={formattedSettings}
      sx={sx}
    />
  );
};

export default AutobiaList;